// Read + write helpers for the slots + assets + placements model.
// Everything user-facing flows through here so components keep treating
// appliances as a flat shape (virtual appliance).

export const NAME_BY_TYPE = {
  hvac: 'HVAC / Furnace',
  refrigerator: 'Refrigerator',
  water_heater: 'Water Heater',
  washer: 'Washer',
  dryer: 'Dryer',
  dishwasher: 'Dishwasher',
  oven: 'Oven / Range',
  microwave: 'Microwave',
  garbage_disposal: 'Garbage Disposal',
  smoke_detector: 'Smoke Detector',
  garage_door: 'Garage Door',
  other: 'Other',
}

const PLACEMENT_SELECT = `
  id, slot_id, asset_id, installed_date, removed_date, cost_history, created_by, created_at,
  slot:appliance_slots ( id, home_id, type, location, nickname, task_dates, manual_url ),
  asset:appliance_assets (
    id, make, model, serial, manufactured, type,
    specifications, spec_source, photo_url, photos,
    energy_star, energy_star_tier, est_annual_energy_cost, energy_star_confidence,
    notes, completed_tasks, parts_scan_status, parts_scan_found,
    photo_uploaded_at, status, data_plate_unavailable
  )
`

export function toVirtual(placement) {
  if (!placement) return null
  const slot = placement.slot || {}
  const asset = placement.asset || {}
  const type = slot.type || asset.type || null
  return {
    id: placement.id,
    placement_id: placement.id,
    slot_id: slot.id || placement.slot_id,
    asset_id: asset.id || placement.asset_id,
    home_id: slot.home_id || null,
    type,
    location: slot.location || null,
    nickname: slot.nickname || null,
    task_dates: slot.task_dates || {},
    manual_url: slot.manual_url || null,
    name: NAME_BY_TYPE[type] || type || 'Appliance',
    // asset fields
    make: asset.make || null,
    model: asset.model || null,
    serial: asset.serial || null,
    manufactured: asset.manufactured || null,
    specifications: asset.specifications || null,
    spec_source: asset.spec_source || null,
    photo_url: asset.photo_url || null,
    photos: asset.photos || [],
    energy_star: asset.energy_star ?? null,
    energy_star_tier: asset.energy_star_tier || null,
    est_annual_energy_cost: asset.est_annual_energy_cost ?? null,
    energy_star_confidence: asset.energy_star_confidence || null,
    notes: asset.notes || null,
    completed_tasks: asset.completed_tasks || {},
    parts_scan_status: asset.parts_scan_status || null,
    parts_scan_found: asset.parts_scan_found ?? 0,
    photo_uploaded_at: asset.photo_uploaded_at || null,
    status: asset.status || 'good',
    data_plate_unavailable: asset.data_plate_unavailable === true,
    // placement fields
    installed_date: placement.installed_date || null,                                              // user-set; may be null
    documented_date: placement.created_at ? String(placement.created_at).split('T')[0] : null,    // auto from created_at, readonly
    cost_history: placement.cost_history || [],
    created_at: placement.created_at || null,
    created_by: placement.created_by || null,
  }
}

// ── READS ───────────────────────────────────────────────────────────────

export async function fetchAppliancesForHome(supabase, homeId) {
  if (!homeId) return []
  const { data, error } = await supabase
    .from('appliance_placements')
    .select(PLACEMENT_SELECT)
    .is('removed_date', null)
    .eq('slot.home_id', homeId)
    .order('created_at', { ascending: false })
  if (error) { console.warn('[applianceQueries] fetchAppliancesForHome', error); return [] }
  // PostgREST inner-table filter (.eq on joined slot) returns rows where slot is null when filter doesn't match;
  // filter client-side to be safe.
  return (data || []).filter(p => p.slot && p.slot.home_id === homeId).map(toVirtual)
}

export async function fetchAppliancesForHomeByType(supabase, homeId, type) {
  const all = await fetchAppliancesForHome(supabase, homeId)
  return all.filter(a => a.type === type)
}

export async function fetchApplianceById(supabase, placementId) {
  if (!placementId) return null
  const { data, error } = await supabase
    .from('appliance_placements')
    .select(PLACEMENT_SELECT)
    .eq('id', placementId)
    .single()
  if (error) { console.warn('[applianceQueries] fetchApplianceById', error); return null }
  return toVirtual(data)
}

export async function fetchAllActivePlacements(supabase) {
  const { data, error } = await supabase
    .from('appliance_placements')
    .select(PLACEMENT_SELECT)
    .is('removed_date', null)
    .order('created_at', { ascending: false })
  if (error) { console.warn('[applianceQueries] fetchAllActivePlacements', error); return [] }
  return (data || []).map(toVirtual)
}

// ── WRITES ──────────────────────────────────────────────────────────────

// Atomic 3-table insert via RPC. Returns the virtual appliance.
export async function createAppliance(supabase, {
  homeId, type, location, nickname,
  make, model, serial, manufactured,
  specifications, spec_source,
  photo_url, photos,
  energy_star, energy_star_tier, est_annual_energy_cost, energy_star_confidence,
  notes, status,
  data_plate_unavailable,
  createdBy,
}) {
  const assetFields = {
    make, model, serial, manufactured,
    specifications, spec_source,
    photo_url, photos,
    energy_star, energy_star_tier, est_annual_energy_cost, energy_star_confidence,
    notes, status,
    data_plate_unavailable,
  }
  // jsonb param: strip undefined so nullif() in the RPC behaves correctly
  const cleanedFields = {}
  for (const [k, v] of Object.entries(assetFields)) {
    if (v !== undefined) cleanedFields[k] = v
  }
  const { data: placementId, error } = await supabase.rpc('create_appliance_full', {
    p_home_id: homeId,
    p_type: type,
    p_location: location || null,
    p_nickname: nickname || null,
    p_asset_fields: cleanedFields,
    p_created_by: createdBy || null,
  })
  if (error) { console.error('[applianceQueries] createAppliance', error); throw error }
  return fetchApplianceById(supabase, placementId)
}

// Atomic replacement via RPC: ends old placement, creates new asset + placement in same slot.
export async function markApplianceAsReplaced(supabase, placementId, newApplianceData) {
  const cleanedFields = {}
  for (const [k, v] of Object.entries(newApplianceData || {})) {
    if (v !== undefined) cleanedFields[k] = v
  }
  const { data: { session } } = await supabase.auth.getSession()
  const { data: newPlacementId, error } = await supabase.rpc('mark_appliance_replaced', {
    p_old_placement_id: placementId,
    p_new_asset_fields: cleanedFields,
    p_created_by: session?.user?.id || null,
  })
  if (error) { console.warn('[applianceQueries] markApplianceAsReplaced', error); throw error }
  return fetchApplianceById(supabase, newPlacementId)
}

async function resolveSlotAndAssetIds(supabase, placementId) {
  const { data, error } = await supabase
    .from('appliance_placements')
    .select('slot_id, asset_id')
    .eq('id', placementId)
    .single()
  if (error || !data) throw error || new Error('Placement not found')
  return { slotId: data.slot_id, assetId: data.asset_id }
}

// All three updaters chain .select() so PostgREST returns the affected rows.
// This converts the silent "RLS allowed UPDATE but matched 0 rows" failure mode
// into an explicit error the caller can surface to the user.
const DEV = (typeof process !== 'undefined') && process.env && process.env.NODE_ENV === 'development'

export async function updateApplianceSlot(supabase, placementId, slotFields) {
  if (DEV) console.log('[applianceQueries] updateApplianceSlot →', { placementId, slotFields })
  const { slotId } = await resolveSlotAndAssetIds(supabase, placementId)
  const { data, error } = await supabase.from('appliance_slots').update(slotFields).eq('id', slotId).select()
  if (DEV) console.log('[applianceQueries] updateApplianceSlot result', { slotId, returnedRows: data?.length, data, error })
  if (error) { console.warn('[applianceQueries] updateApplianceSlot', error); throw error }
  if (!data || data.length === 0) { const e = new Error('Slot update affected 0 rows (RLS or missing row)'); console.warn('[applianceQueries] updateApplianceSlot', e); throw e }
}

export async function updateApplianceAsset(supabase, placementId, assetFields) {
  if (DEV) console.log('[applianceQueries] updateApplianceAsset →', { placementId, assetFields })
  const { assetId } = await resolveSlotAndAssetIds(supabase, placementId)
  const { data, error } = await supabase.from('appliance_assets').update(assetFields).eq('id', assetId).select()
  if (DEV) console.log('[applianceQueries] updateApplianceAsset result', { assetId, returnedRows: data?.length, data, error })
  if (error) { console.warn('[applianceQueries] updateApplianceAsset', error); throw error }
  if (!data || data.length === 0) { const e = new Error('Asset update affected 0 rows (RLS or missing row)'); console.warn('[applianceQueries] updateApplianceAsset', e); throw e }
}

export async function updateAppliancePlacement(supabase, placementId, placementFields) {
  if (DEV) console.log('[applianceQueries] updateAppliancePlacement →', { placementId, placementFields })
  const { data, error } = await supabase.from('appliance_placements').update(placementFields).eq('id', placementId).select()
  if (DEV) console.log('[applianceQueries] updateAppliancePlacement result', { placementId, returnedRows: data?.length, data, error })
  if (error) { console.warn('[applianceQueries] updateAppliancePlacement', error); throw error }
  if (!data || data.length === 0) { const e = new Error('Placement update affected 0 rows (RLS or missing row)'); console.warn('[applianceQueries] updateAppliancePlacement', e); throw e }
}

// Hard-delete the placement only — slot + asset persist (slot becomes empty, asset becomes orphaned-but-historical).
export async function deleteAppliance(supabase, placementId) {
  const { error } = await supabase.from('appliance_placements').delete().eq('id', placementId)
  if (error) { console.warn('[applianceQueries] deleteAppliance', error); throw error }
}
